import { encodeBase64 } from '@std/encoding/base64';
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useFS } from '@/hooks/useFS';
import { addConsoleMessage, type ConsoleMessage } from '@/lib/consoleMessages';
import { useConsoleError } from '@/hooks/useConsoleError';
import { useBuildProject } from '@/hooks/useBuildProject';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { FolderOpen, ArrowLeft, Play, Loader2, ChevronDown, Code, Rocket, Terminal, MousePointer2, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { ConsolePanel } from '@/components/ConsolePanel';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GitStatusIndicator } from '@/components/GitStatusIndicator';
import { BrowserAddressBar, type DeviceMode } from '@/components/ui/browser-address-bar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { FileTree } from './FileTree';
import { FileEditor } from './FileEditor';
import { DeployDialog } from '@/components/DeployDialog';
import { Terminal as TerminalComponent } from '@/components/Terminal';
import { SaveCodeButton } from '@/components/SaveCodeButton';
import { useSearchParams } from 'react-router-dom';
import { useAppContext } from '@/hooks/useAppContext';

interface PreviewPaneProps {
  projectId: string;
  activeTab: 'preview' | 'code';
  onToggleView?: () => void;
  projectName?: string;
  onFirstInteraction?: () => void;
  isPreviewable?: boolean;
  isChatMinimized?: boolean;
  onToggleChatMinimized?: () => void;
  onRequestErrorFix?: (errorMessage: string) => void;
}

export interface PreviewPaneRef {
  getIframeRef: () => React.RefObject<HTMLIFrameElement>;
}

interface JSONRPCRequest {
  jsonrpc: '2.0';
  method: string;
  params: {
    request: {
      url: string;
      method: string;
      headers: Record<string, string>;
      body: string | null;
    }
  };
  id: number;
}

interface JSONRPCResponse {
  jsonrpc: '2.0';
  result?: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string | null;
  }
  error?: {
    code: number;
    message: string;
    data?: Record<string, unknown>;
  };
  id: number;
}

export const PreviewPane = forwardRef<PreviewPaneRef, PreviewPaneProps>(({ projectId, activeTab, onToggleView, projectName, onFirstInteraction: _onFirstInteraction, isPreviewable = true, isChatMinimized = false, onToggleChatMinimized, onRequestErrorFix }, ref) => {
  const { t } = useTranslation();
  const { config } = useAppContext();
  const { previewDomain } = config;
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [mobileCodeView, setMobileCodeView] = useState<'explorer' | 'editor' | 'terminal'>('explorer');
  const [desktopCodeView, setDesktopCodeView] = useState<'files' | 'terminal'>('files');
  const isMobile = useIsMobile();
  const [hasBuiltProject, setHasBuiltProject] = useState(false);
  const [currentPath, setCurrentPath] = useState('/');
  const [navigationHistory, setNavigationHistory] = useState<string[]>(['/']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');

  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [inspectorEnabled, setInspectorEnabled] = useState(false);
  const [selectedElementInfo, setSelectedElementInfo] = useState<Record<string, unknown> | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Expose iframe ref through useImperativeHandle
  useImperativeHandle(ref, () => ({
    getIframeRef: () => iframeRef,
  }), []);

  const { fs } = useFS();
  const projectsManager = useProjectsManager();
  const [iframeKey, setIframeKey] = useState(0);

  // Use console error state from provider
  const { clearErrors } = useConsoleError();

  const { mutate: buildProject, isPending: isBuildLoading } = useBuildProject(projectId);

  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldBuild, setShouldBuild] = useState(false);
  const [saveCodeOpen, setSaveCodeOpen] = useState(false);

  const handleBuildProject = useCallback(() => {
    // Clear console errors when starting a new build for a fresh slate
    clearErrors();
    buildProject(undefined, {
      onError: (error) => {
        console.error('Build failed:', error);
      }
    });
  }, [buildProject, clearErrors]);

  // Handle "build" and "saveCode" URL parameters on initial load
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    let hasChanges = false;

    if (searchParams.has('build')) {
      setShouldBuild(true);
      newSearchParams.delete('build');
      hasChanges = true;
    }

    if (searchParams.has('saveCode')) {
      setSaveCodeOpen(true);
      newSearchParams.delete('saveCode');
      hasChanges = true;
    }

    if (hasChanges) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Build automatically if "build" parameter was present
  useEffect(() => {
    if (shouldBuild && isPreviewable && !isBuildLoading) {
      setShouldBuild(false);
      handleBuildProject();
    }
  }, [isBuildLoading, isPreviewable, handleBuildProject, shouldBuild]);

  const loadFileContent = useCallback(async (filePath: string) => {
    setIsLoading(true);
    try {
      const content = await projectsManager.readFile(projectId, filePath);
      setFileContent(content);
    } catch (_error) {
      console.error('Failed to load file:', _error);
      setFileContent('');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, projectsManager]);

  const getContentType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      'html': 'text/html',
      'js': 'application/javascript',
      'mjs': 'application/javascript',
      'css': 'text/css',
      'json': 'application/json',
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'svg': 'image/svg+xml',
      'ico': 'image/x-icon',
      'woff': 'font/woff',
      'woff2': 'font/woff2',
      'ttf': 'font/ttf',
      'eot': 'application/vnd.ms-fontobject',
      'txt': 'text/plain',
      'xml': 'application/xml',
      'pdf': 'application/pdf',
      'zip': 'application/zip',
    };
    return mimeTypes[ext || ''] || 'application/octet-stream';
  };

  const checkForBuiltProject = useCallback(async () => {
    try {
      const exists = await projectsManager.fileExists(projectId, 'dist/index.html');
      setHasBuiltProject(exists);
    } catch (error) {
      console.error('Failed to check for built project:', error);
      setHasBuiltProject(false);
    }
  }, [projectId, projectsManager]);

  // Legacy refresh function for build completion events
  const refreshIframeLegacy = useCallback(() => {
    if (iframeRef.current) {
      // Force reload the iframe by updating its src
      iframeRef.current.src = '/';
      // Use a small timeout to ensure the src is cleared before setting it back
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = `https://${projectId}.${previewDomain}/`;
        }
      }, 10);
    }
  }, [projectId, previewDomain]);

  const sendResponse = useCallback((message: JSONRPCResponse) => {
    if (iframeRef.current?.contentWindow) {

      const targetOrigin = `https://${projectId}.${previewDomain}`;
      iframeRef.current.contentWindow.postMessage(message, targetOrigin);
    }
  }, [projectId, previewDomain]);

  const sendError = useCallback((message: JSONRPCResponse) => {
    if (iframeRef.current?.contentWindow) {
      const targetOrigin = `https://${projectId}.${previewDomain}`;
      iframeRef.current.contentWindow.postMessage(message, targetOrigin);
    }
  }, [projectId, previewDomain]);

  const handleConsoleMessage = useCallback((message: {
    jsonrpc: '2.0';
    method: 'console';
    params: {
      level: 'log' | 'warn' | 'error' | 'info' | 'debug';
      message: string;
    };
  }) => {
    const { params } = message;

    // Normalize level to ensure it's one of our supported types
    let normalizedLevel: ConsoleMessage['level'] = 'log';
    if (['log', 'warn', 'error', 'info', 'debug'].includes(params.level)) {
      normalizedLevel = params.level as ConsoleMessage['level'];
    }

    // Add to console messages
    addConsoleMessage(normalizedLevel, params.message);

    // Log to parent console for debugging with appropriate level
    console[normalizedLevel](`[IFRAME ${params.level.toUpperCase()}] ${params.message}`);
  }, []);

  const handleUpdateNavigationState = useCallback((message: {
    jsonrpc: '2.0';
    method: 'updateNavigationState';
    params: {
      currentUrl: string;
      canGoBack: boolean;
      canGoForward: boolean;
    };
  }) => {
    const { params } = message;

    try {
      // params.currentUrl is now just a semantic path (e.g., "/about", "/contact?param=value#section")
      const path = params.currentUrl;

      setCurrentPath(path);

      // Update navigation history if this is a new navigation
      // But only if it's different from current path (avoid duplicates)
      if (path !== navigationHistory[historyIndex]) {
        const newHistory = navigationHistory.slice(0, historyIndex + 1);
        newHistory.push(path);
        setNavigationHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    } catch (error) {
      console.error('Failed to handle navigation state:', params.currentUrl, error);
    }
  }, [historyIndex, navigationHistory]);

  const handleElementSelected = useCallback((message: {
    jsonrpc: '2.0';
    method: 'elementSelected';
    params: Record<string, unknown>;
  }) => {
    const { params } = message;
    console.log('[Element Inspector] Element selected in parent:', params);
    setSelectedElementInfo(params);
  }, []);

  const handleInspectorDisabled = useCallback(() => {
    console.log('[Element Inspector] Inspector disabled from iframe');
    setInspectorEnabled(false);
    setSelectedElementInfo(null);
  }, []);

  const sendNavigationCommand = useCallback((method: string, params?: Record<string, unknown>) => {
    if (iframeRef.current?.contentWindow) {
      const message = {
        jsonrpc: '2.0',
        method,
        params: params || {},
        id: Date.now()
      };
      const targetOrigin = `https://${projectId}.${previewDomain}`;
      iframeRef.current.contentWindow.postMessage(message, targetOrigin);
    }
  }, [projectId, previewDomain]);

  const navigateIframe = useCallback((url: string) => {
    // Send semantic path directly (e.g., "/about", "/contact")
    // The iframe's NavigationHandler will handle this as semantic navigation
    sendNavigationCommand('navigate', { url });
  }, [sendNavigationCommand]);

  const refreshIframe = useCallback(() => {
    // Clear console errors when refreshing for a fresh slate
    clearErrors();
    sendNavigationCommand('refresh');
  }, [sendNavigationCommand, clearErrors]);

  const toggleInspector = useCallback(() => {
    const newState = !inspectorEnabled;
    setInspectorEnabled(newState);

    if (newState) {
      sendNavigationCommand('enableInspector');
      setSelectedElementInfo(null); // Clear previous selection
    } else {
      sendNavigationCommand('disableInspector');
      setSelectedElementInfo(null);
    }
  }, [inspectorEnabled, sendNavigationCommand]);

  // Handle device mode change - reload iframe to apply new user agent
  useEffect(() => {
    if (hasBuiltProject) {
      // Force iframe reload by changing its key
      setIframeKey(prev => prev + 1);
    }
  }, [deviceMode, hasBuiltProject]);

  const goBackIframe = useCallback(() => {
    if (historyIndex > 0) {
      // Move back in history
      const newIndex = historyIndex - 1;
      const targetPath = navigationHistory[newIndex];

      setHistoryIndex(newIndex);
      setCurrentPath(targetPath);

      // Tell iframe to navigate to this path
      sendNavigationCommand('navigate', { url: targetPath });
    }
  }, [historyIndex, navigationHistory, sendNavigationCommand]);

  const goForwardIframe = useCallback(() => {
    if (historyIndex < navigationHistory.length - 1) {
      // Move forward in history
      const newIndex = historyIndex + 1;
      const targetPath = navigationHistory[newIndex];

      setHistoryIndex(newIndex);
      setCurrentPath(targetPath);

      // Tell iframe to navigate to this path
      sendNavigationCommand('navigate', { url: targetPath });
    }
  }, [historyIndex, navigationHistory, sendNavigationCommand]);

  const handleFetch = useCallback(async (request: JSONRPCRequest) => {
    const { params, id } = request;
    const { request: fetchRequest } = params;

    try {
      // Parse the URL and validate origin
      const url = new URL(fetchRequest.url);
      const expectedOrigin = `https://${projectId}.${previewDomain}`;

      if (url.origin !== expectedOrigin) {
        console.log(`Invalid origin: ${url.origin}, expected: ${expectedOrigin}`);
        sendError({
          jsonrpc: '2.0',
          error: {
            code: -32003,
            message: 'Invalid URL - origin mismatch',
            data: { url: fetchRequest.url, expectedOrigin }
          },
          id
        });
        return;
      }

      const path = url.pathname;
      const filePath = path;

      // Skip SPA fallback for static assets (files with extensions)
      const isStaticAsset = /\.[a-zA-Z0-9]+$/.test(path);

      // SPA routing: try to serve the exact file first
      try {
        const bytes = await projectsManager.readFileBytes(projectId, 'dist' + filePath);
        console.log(`Serving file: ${filePath}`);

        sendResponse({
          jsonrpc: '2.0',
          result: {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': getContentType(filePath),
              'Cache-Control': 'no-cache',
            },
            body: encodeBase64(bytes),
          },
          id
        });
        return;
      } catch {

        // For static assets, return 404 immediately
        if (isStaticAsset) {

          sendResponse({
            jsonrpc: '2.0',
            result: {
              status: 404,
              statusText: 'Not Found',
              headers: {
                'Content-Type': 'text/plain',
              },
              body: encodeBase64(`Static asset not found: ${path}`),
            },
            id
          });
          return;
        }
      }

      // SPA fallback: serve index.html for non-file requests (SPA routing)
      try {
        const bytes = await projectsManager.readFileBytes(projectId, 'dist/index.html');
        console.log(`Serving index.html fallback for: ${path}`);

        sendResponse({
          jsonrpc: '2.0',
          result: {
            status: 200,
            statusText: 'OK',
            headers: {
              'Content-Type': 'text/html',
              'Cache-Control': 'no-cache',
            },
            body: encodeBase64(bytes),
          },
          id
        });
      } catch {
        // Even index.html doesn't exist
        sendResponse({
          jsonrpc: '2.0',
          result: {
            status: 404,
            statusText: 'Not Found',
            headers: {
              'Content-Type': 'text/plain',
            },
            body: encodeBase64(`File not found: ${path}`),
          },
          id
        });
      }
    } catch (error) {
      console.error('Error processing fetch request:', error);
      sendError({
        jsonrpc: '2.0',
        error: {
          code: -32002,
          message: 'Request processing error',
          data: { url: fetchRequest.url, error: String(error) }
        },
        id
      });
    }
  }, [projectId, projectsManager, sendResponse, sendError, previewDomain]);

  // Setup messaging protocol for iframe communication
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security
      const expectedOrigin = `https://${projectId}.${previewDomain}`;
      if (event.origin !== expectedOrigin) {
        console.log(`Ignoring message from unexpected origin: ${event.origin}, expected: ${expectedOrigin}`);
        return;
      }

      const message = event.data;
      console.log('Received message from iframe:', message);
      if (message.jsonrpc === '2.0' && message.method === 'fetch') {
        handleFetch(message);
      } else if (message.jsonrpc === '2.0' && message.method === 'console') {
        handleConsoleMessage(message);
      } else if (message.jsonrpc === '2.0' && message.method === 'updateNavigationState') {
        handleUpdateNavigationState(message);
      } else if (message.jsonrpc === '2.0' && message.method === 'elementSelected') {
        handleElementSelected(message);
      } else if (message.jsonrpc === '2.0' && message.method === 'inspectorDisabled') {
        handleInspectorDisabled();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleFetch, handleConsoleMessage, handleUpdateNavigationState, handleElementSelected, handleInspectorDisabled, projectId, previewDomain]);

  useEffect(() => {
    if (selectedFile) {
      loadFileContent(selectedFile);
    }
  }, [selectedFile, loadFileContent]);

  // Reset selected file and navigation history when projectId changes
  const prevProjectIdRef = useRef<string>();
  useEffect(() => {
    setSelectedFile(null);
    setFileContent('');
    setMobileCodeView('explorer');
    setDesktopCodeView('files');

    // Reset navigation history and state
    setCurrentPath('/');
    setNavigationHistory(['/']);
    setHistoryIndex(0);

    // Only clear console messages when projectId actually changes (not on initial mount)
    if (prevProjectIdRef.current && prevProjectIdRef.current !== projectId) {
      clearErrors();
    }
    prevProjectIdRef.current = projectId;
  }, [projectId, clearErrors]);

  useEffect(() => {
    checkForBuiltProject();
  }, [checkForBuiltProject]);

  // Listen for build completion events to refresh the iframe
  useEffect(() => {
    const handleBuildComplete = (event: CustomEvent) => {
      if (event.detail?.projectId === projectId) {
        console.log('Build completed for project, refreshing preview');
        // Check for built project and refresh iframe
        checkForBuiltProject();
        refreshIframeLegacy();
      }
    };

    window.addEventListener('buildComplete', handleBuildComplete as EventListener);
    return () => window.removeEventListener('buildComplete', handleBuildComplete as EventListener);
  }, [projectId, checkForBuiltProject, refreshIframeLegacy]);

  const handleFileSelect = (filePath: string) => {
    setSelectedFile(filePath);
    if (isMobile) {
      setMobileCodeView('editor');
    }
  };

  const handleFileSave = async (content: string) => {
    if (!selectedFile) return;

    try {
      await fs.writeFile(`/projects/${projectId}/${selectedFile}`, content);
      setFileContent(content);

      // Automatically trigger a rebuild after saving a file
      if (isPreviewable) {
        console.log('File saved, triggering rebuild...');
        handleBuildProject();
      }
    } catch (error) {
      console.error('Failed to save file:', error);
    }
  };



  const Menu = () => {
    const isAnyLoading = isBuildLoading;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1"
          >
            <span className="text-sm">Publish</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 sm:w-48">
          <DropdownMenuItem
            onClick={handleBuildProject}
            disabled={isBuildLoading}
            className="gap-2"
          >
            {isBuildLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {isBuildLoading ? 'Building...' : 'Build'}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDeployDialogOpen(true)}
            disabled={isAnyLoading}
            className="gap-2"
          >
            <Rocket className="h-4 w-4" />
            Deploy
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="h-full">
      <Tabs value={activeTab} className="h-full">
        {isPreviewable && (
          <TabsContent value="preview" className="h-full mt-0">
            <div className="h-full w-full flex flex-col relative">
              {/* Always show browser address bar */}
              <div className="h-12 flex items-center w-full">
                <BrowserAddressBar
                  currentPath={currentPath}
                  onNavigate={hasBuiltProject ? navigateIframe : undefined}
                  onRefresh={hasBuiltProject ? refreshIframe : undefined}
                  onBack={hasBuiltProject ? goBackIframe : undefined}
                  onForward={hasBuiltProject ? goForwardIframe : undefined}
                  canGoBack={hasBuiltProject && historyIndex > 0}
                  canGoForward={hasBuiltProject && historyIndex < navigationHistory.length - 1}
                  deviceMode={deviceMode}
                  onDeviceModeChange={setDeviceMode}
                  leftContent={(!isMobile && onToggleChatMinimized) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onToggleChatMinimized}
                      className="h-8 w-8 p-0"
                      title={isChatMinimized ? 'Show chat' : 'Hide chat'}
                    >
                      {isChatMinimized ? (
                        <PanelLeftOpen className="h-4 w-4" />
                      ) : (
                        <PanelLeftClose className="h-4 w-4" />
                      )}
                    </Button>
                  ) : undefined}
                  extraContent={(
                    <div className="flex items-center gap-1">
                      <SaveCodeButton projectId={projectId} projectName={projectName} />
                      {(!isMobile && onToggleView && isPreviewable) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={onToggleView}
                          className="h-8 gap-2"
                        >
                          <Code className="h-4 w-4" />
                        </Button>
                      )}
                      <ConsolePanel onRequestFix={onRequestErrorFix} onRefresh={hasBuiltProject ? refreshIframe : undefined} />
                      <Menu />
                    </div>
                  )}
                />
              </div>

              {/* Content area */}
              <div className="flex-1 bg-muted/30 flex items-center justify-center overflow-auto preview-scrollbar">
                {hasBuiltProject ? (
                  <div
                    className={cn(
                      "shadow-2xl transition-all duration-300",
                      deviceMode === 'desktop' && "w-full h-full bg-background",
                      deviceMode === 'tablet' && "w-[768px] h-full max-w-full rounded-lg border bg-background",
                      deviceMode === 'mobile' && "rounded-2xl border-8 border-gray-800 bg-gray-800 p-0"
                    )}
                  >
                    <iframe
                      key={iframeKey}
                      ref={iframeRef}
                      src={`https://${projectId}.${previewDomain}/?device=${deviceMode}`}
                      className={cn(
                        "border-0 bg-background",
                        deviceMode === 'desktop' && "w-full h-full",
                        deviceMode === 'tablet' && "w-full h-full rounded-lg",
                        deviceMode === 'mobile' && "w-[394px] h-[852px] rounded-xl"
                      )}
                      title="Project Preview"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center bg-muted">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold mb-2">{t('projectPreview')}</h3>
                      <p className="text-muted-foreground mb-4">
                        {t('buildProjectToSeePreview')}
                      </p>
                      <Button
                        onClick={handleBuildProject}
                        disabled={isBuildLoading}
                        variant="outline"
                        className="gap-2"
                      >
                        {isBuildLoading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Building...
                          </>
                        ) : (
                          <>
                            <Play className="h-5 w-5" />
                            Build Project
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Build loading overlay */}
              {isBuildLoading && (
                <div className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
                  <div className="bg-background/90 border rounded-lg p-4 shadow-lg flex items-center gap-3">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-sm font-medium">Building project...</span>
                  </div>
                </div>
              )}

              {/* Element Inspector Info Panel */}
              {inspectorEnabled && (
                <div className="absolute top-14 left-4 bg-background/95 backdrop-blur-sm border rounded-lg p-3 shadow-lg z-20 max-w-md">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <MousePointer2 className="h-4 w-4 text-primary" />
                      Element Inspector
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleInspector}
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  {selectedElementInfo ? (
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="font-medium text-muted-foreground">Tag:</span>{' '}
                        <code className="bg-muted px-1 py-0.5 rounded">{selectedElementInfo.tagName as string}</code>
                      </div>
                      {selectedElementInfo.id && (
                        <div>
                          <span className="font-medium text-muted-foreground">ID:</span>{' '}
                          <code className="bg-muted px-1 py-0.5 rounded">{selectedElementInfo.id as string}</code>
                        </div>
                      )}
                      {selectedElementInfo.className && (
                        <div>
                          <span className="font-medium text-muted-foreground">Class:</span>{' '}
                          <code className="bg-muted px-1 py-0.5 rounded text-xs break-all">
                            {selectedElementInfo.className as string}
                          </code>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-muted-foreground">Selector:</span>
                        <code className="block bg-muted px-2 py-1 rounded mt-1 text-xs break-all max-h-20 overflow-y-auto">
                          {selectedElementInfo.path as string}
                        </code>
                      </div>
                      <details className="cursor-pointer">
                        <summary className="font-medium text-muted-foreground">Styles</summary>
                        <div className="bg-muted p-2 rounded mt-1 max-h-40 overflow-y-auto text-xs space-y-1">
                          {Object.entries((selectedElementInfo.styles as Record<string, string>) || {}).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-muted-foreground">{key}:</span> {value}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Hover over elements and click to inspect. Press <kbd className="bg-muted px-1 rounded">ESC</kbd> to exit.
                    </p>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        )}

        <TabsContent value="code" className="h-full mt-0">
          {isMobile ? (
            <div className="h-full flex flex-col min-h-0">
              {mobileCodeView === 'explorer' ? (
                <div className="flex-1 relative min-h-0">
                  <ScrollArea className="h-full">
                    <ScrollBar orientation="horizontal" />
                    <div className="min-w-max">
                      <FileTree
                        projectId={projectId}
                        onFileSelect={handleFileSelect}
                        selectedFile={selectedFile}
                      />
                    </div>
                  </ScrollArea>
                  {/* Floating Terminal Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMobileCodeView('terminal')}
                    className="fixed right-4 bottom-14 mb-safe z-20 size-12 p-0 rounded-full shadow-lg bg-background border-2"
                  >
                    <Terminal className="h-6 w-6" />
                  </Button>
                </div>
              ) : mobileCodeView === 'terminal' ? (
                <>
                  <div className="h-12 px-3 flex items-center gap-2 bg-black text-purple-400 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMobileCodeView('explorer')}
                      className="p-1 hover:bg-transparent hover:text-white"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="font-semibold">
                      Terminal
                    </h3>
                  </div>
                  <div className="flex-1 min-h-0">
                    <TerminalComponent projectId={projectId} />
                  </div>
                </>
              ) : (
                <>
                  <div className="p-3 border-b bg-gradient-to-r from-primary/5 to-accent/5 flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setMobileCodeView('explorer')}
                      className="p-1"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <h3 className="font-semibold flex-1 truncate bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      {selectedFile ? selectedFile.split('/').pop() : t('fileEditor')}
                    </h3>
                    <SaveCodeButton projectId={projectId} projectName={projectName} />
                    <GitStatusIndicator projectId={projectId} />
                  </div>
                  <div className="flex-1">
                    {selectedFile ? (
                      <FileEditor
                        filePath={selectedFile}
                        content={fileContent}
                        onSave={handleFileSave}
                        isLoading={isLoading}
                        projectId={projectId}
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center">
                          <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">
                            {t('selectFileFromExplorer')}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setMobileCodeView('explorer')}
                            className="mt-4"
                          >
                            {t('openFileExplorer')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {/* Code view header with back button */}
              {!isMobile && onToggleView && isPreviewable && (
                <div className="h-12 px-4 border-b flex items-center bg-gradient-to-r from-muted/20 to-background">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleView}
                    className="gap-2 text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    {t('backToPreview')}
                  </Button>
                  <div className="flex-1" />
                  <SaveCodeButton projectId={projectId} projectName={projectName} />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDesktopCodeView(desktopCodeView === 'terminal' ? 'files' : 'terminal')}
                    className="gap-2 text-muted-foreground hover:text-foreground"
                  >
                    <Terminal className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {/* Code view header without back button for non-previewable projects */}
              {!isMobile && !isPreviewable && (
                <div className="h-12 px-4 border-b flex items-center bg-gradient-to-r from-muted/20 to-background">
                  <div className="flex-1" />
                  <SaveCodeButton projectId={projectId} projectName={projectName} />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDesktopCodeView(desktopCodeView === 'terminal' ? 'files' : 'terminal')}
                    className="gap-2 text-muted-foreground hover:text-foreground"
                  >
                    <Terminal className="h-4 w-4" />
                  </Button>
                  <GitStatusIndicator projectId={projectId} />
                </div>
              )}

              <div className="flex-1 flex min-h-0">
                {desktopCodeView === 'terminal' ? (
                  <div className="flex-1">
                    <TerminalComponent projectId={projectId} />
                  </div>
                ) : (
                  <>
                    <div className="w-1/3 border-r flex flex-col">
                      <ScrollArea className="flex-1">
                        <ScrollBar orientation="horizontal" />
                        <div className="min-w-max">
                          <FileTree
                            projectId={projectId}
                            onFileSelect={handleFileSelect}
                            selectedFile={selectedFile}
                          />
                        </div>
                      </ScrollArea>
                    </div>

                    <div className="flex-1">
                      {selectedFile ? (
                        <FileEditor
                          filePath={selectedFile}
                          content={fileContent}
                          onSave={handleFileSave}
                          isLoading={isLoading}
                          projectId={projectId}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-muted-foreground">
                              {t('selectFileFromExplorer')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Deploy Dialog */}
      {projectName && (
        <DeployDialog
          projectId={projectId}
          projectName={projectName}
          open={deployDialogOpen}
          onOpenChange={setDeployDialogOpen}
        />
      )}
    </div>
  );
});